import React from 'react'
import Image from 'next/image'
import { classNames } from 'app/utils/classes'
import { UserCircleIcon } from '@heroicons/react/20/solid'

interface StaticImageProps {
  imageLink?: string | null
  alt?: string
  relative?: boolean
  noS3?: boolean
  rounded?:
    | 'rounded'
    | 'rounded-full'
    | 'rounded-lg'
    | 'rounded-md'
    | 'rounded-top'
    | 'rounded-tl'
    | 'rounded-tr'
    | 'rounded-bl'
    | 'rounded-br'
    | 'rounded-t'
    | 'rounded-t-lg'
    | 'sm:rounded-lg rounded-t'
}

const IMAGE_PLACEHOLDER = 'placeholder.jpg'

const StaticImage = ({
  imageLink,
  alt,
  relative = false,
  noS3 = false,
  rounded,
}: StaticImageProps) => (
  <div
    className={`h-full w-full object-cover object-center sm:h-full sm:w-full ${
      relative ? 'relative' : ''
    }`}
  >
    {noS3 ? (
      <Image
        src={
          imageLink ??
          `${process.env.NEXT_PUBLIC_STATIC_URL}/${IMAGE_PLACEHOLDER}`
        }
        alt={alt || ''}
        className={classNames(rounded ? rounded : '', 'h-full w-full')}
        style={{ objectFit: 'cover' }}
        width={200}
        height={200}
      />
    ) : (
      <Image
        src={`${process.env.NEXT_PUBLIC_STATIC_URL}/${
          imageLink ?? IMAGE_PLACEHOLDER
        }`}
        alt={alt || ''}
        className={classNames(rounded ? rounded : '', 'h-full w-full')}
        style={{ objectFit: 'cover' }}
        width={500}
        height={300}
      />
    )}
  </div>
)

interface CheckoutImageProps {
  imageLink?: string | null
  alt?: string
  relative?: boolean
  noS3?: boolean
}

const CheckoutImage = ({
  imageLink,
  alt,
  relative = false,
  noS3 = false,
}: CheckoutImageProps) => (
  <div
    className={`h-full w-full object-cover object-center sm:h-full sm:w-full ${
      relative ? 'relative' : ''
    }`}
  >
    {noS3 ? (
      <Image
        src={
          imageLink ??
          `${process.env.NEXT_PUBLIC_STATIC_URL}/${IMAGE_PLACEHOLDER}`
        }
        alt={alt || ''}
        className="h-full w-full"
        style={{ objectFit: 'cover' }}
        width={833}
        height={900}
      />
    ) : (
      <Image
        src={`${process.env.NEXT_PUBLIC_STATIC_URL}/${
          imageLink ?? IMAGE_PLACEHOLDER
        }`}
        alt={alt || ''}
        className="h-full w-full"
        style={{ objectFit: 'cover' }}
        width={1000}
        height={1300}
      />
    )}
  </div>
)

interface ProfileImageProps {
  imageLink?: string | null
  alt?: string
  relative?: boolean
}

const ProfileImage = ({
  imageLink,
  alt = 'profile image',
  relative = false,
}: ProfileImageProps) => (
  <div
    className={classNames(
      'h-full w-full rounded-full object-cover object-center sm:h-full sm:w-full',
      relative ? 'relative' : ''
    )}
  >
    {imageLink ? (
      <Image
        src={`${process.env.NEXT_PUBLIC_STATIC_URL}/${imageLink}`}
        layout="fill"
        objectFit="cover"
        alt={alt}
      />
    ) : (
      <UserCircleIcon className="h-full w-full" />
    )}
  </div>
)

interface CompanyLogoProps {
  imageLink?: string | null
  alt?: string
  relative?: boolean
  noS3?: boolean
}

const CompanyLogo = ({
  imageLink,
  alt,
  relative = false,
  noS3 = false,
}: CompanyLogoProps) => (
  <div
    className={`h-full w-full object-cover object-center sm:h-full sm:w-full ${
      relative ? 'relative' : ''
    }`}
  >
    {noS3 ? (
      <Image
        src={
          imageLink ??
          `${process.env.NEXT_PUBLIC_STATIC_URL}/${IMAGE_PLACEHOLDER}`
        }
        alt={alt || ''}
        className="h-full w-auto"
        style={{ objectFit: 'cover' }}
        width={500}
        height={500}
      />
    ) : (
      <Image
        src={`${process.env.NEXT_PUBLIC_STATIC_URL}/${
          imageLink ?? IMAGE_PLACEHOLDER
        }`}
        alt={alt || ''}
        className="h-full w-auto"
        style={{ objectFit: 'cover' }}
        width={500}
        height={500}
      />
    )}
  </div>
)

interface ItemPictureProps {
  imageLink?: string | null
  alt?: string
  relative?: boolean
  noS3?: boolean
}

const ItemPicture = ({
  imageLink,
  alt,
  relative = false,
  noS3 = false,
}: ItemPictureProps) => (
  <div
    className={`h-full w-full object-cover object-center sm:h-full sm:w-full ${
      relative ? 'relative' : ''
    }`}
  >
    {noS3 ? (
      <Image
        src={
          imageLink ??
          `${process.env.NEXT_PUBLIC_STATIC_URL}/${IMAGE_PLACEHOLDER}`
        }
        alt={alt || ''}
        className="h-full w-full"
        style={{ objectFit: 'cover' }}
        width={200}
        height={200}
        quality={100}
      />
    ) : (
      <Image
        src={`${process.env.NEXT_PUBLIC_STATIC_URL}/${
          imageLink ?? IMAGE_PLACEHOLDER
        }`}
        alt={alt || ''}
        className="h-full w-full"
        style={{ objectFit: 'cover' }}
        width={200}
        height={200}
        quality={100}
      />
    )}
  </div>
)

export {
  StaticImage,
  CheckoutImage,
  IMAGE_PLACEHOLDER,
  ProfileImage,
  CompanyLogo,
  ItemPicture,
}
